import { Component, inject, input, output, effect } from "@angular/core";

import { StepperModule } from "@progress/kendo-angular-layout";
import {
    SVGIcon,
    xOutlineIcon,
    checkOutlineIcon,
} from "@progress/kendo-svg-icons";

interface IStepper {
    label: string;
    svgIcon: SVGIcon;
    isValid: () => boolean;
    validate: boolean;
}

/**
 * Represents validation status for steps
 */
type ValidationStatus = boolean | null; // true = valid, false = invalid, null = not validated

@Component({
    selector: "app-utility-stepper",
    imports: [StepperModule],
    templateUrl: "./utility-stepper.component.html",
    styleUrl: "./utility-stepper.component.scss",
})
export class UtilityStepperComponent {
  // inputs for the stepper component
    steps = input<IStepper[]>([]);
    currentStep = input<number>(0);
    isLinear = input<boolean>(false);
    // Flag to control when validation should run
    enableValidation = input<boolean>(false);
    // Validation status for each step
    validationStatus = input<ValidationStatus[]>([]);
    // Original icons to reset to
    #originalIcons = new Map<string, SVGIcon>();
    // Event outputs
    stepChange = output<number>();
    stepActivate = output<any>();
    validationStatusChange = output<ValidationStatus[]>();

    // Icons
    readonly errorIcon: SVGIcon = xOutlineIcon;
    readonly successIcon: SVGIcon = checkOutlineIcon;


    constructor() {
        effect(() => {
            // Store original icons whenever steps change
            if (this.steps().length > 0) {
                this.#saveOriginalIcons();
            }
            
            // Apply validation status whenever it changes
            if (this.validationStatus().length > 0) {
                this.#applyValidationStatus();
            }
        });
    }    /**
     * Manually triggers validation of steps
     * @param stepper The stepper instance to validate
     * @returns True if all steps are valid, false otherwise
     */
    triggerValidation(stepper: any): boolean {
        if (!stepper || typeof stepper.validateSteps !== "function") {
            return true;
        }
        
        stepper.validateSteps();
        
        // Check if any step is invalid and update validation status
        const newValidationStatus: ValidationStatus[] = [];
        
        if (this.steps()) {
            for (const step of this.steps()) {
                if (step.validate) {
                    const isValid = step.isValid();
                    newValidationStatus.push(isValid);
                    if (!isValid) {
                        // Found an invalid step
                        // Emit validation status change
                        this.validationStatusChange.emit(newValidationStatus);
                        return false;
                    }
                } else {
                    // Step doesn't require validation
                    newValidationStatus.push(null);
                }
            }
        }
        
        // All steps are valid
        this.validationStatusChange.emit(newValidationStatus);
        return true;
    }    validateSteps(stepper: any) {
        // Only validate if explicitly enabled
        if (!this.enableValidation()) {
            return;
        }
        
        // Call validateSteps on the stepper instance
        if (stepper && typeof stepper.validateSteps === "function") {
            stepper.validateSteps();
            
            // Perform validation and update status
            this.triggerValidation(stepper);
        }
    }

    /**
     * Stores the original icons for each step to be able to reset them later
     */
    #saveOriginalIcons(): void {
        this.#originalIcons.clear();
        this.steps().forEach(step => {
            this.#originalIcons.set(step.label, step.svgIcon);
        });
    }

    /**
     * Applies the validation status to update step icons
     */
    #applyValidationStatus(): void {
        const validationStatusArray = this.validationStatus();
        if (!validationStatusArray || validationStatusArray.length === 0) {
            return;
        }

        this.steps().forEach((step, index) => {
            if (index < validationStatusArray.length) {
                const status = validationStatusArray[index];
                if (status === true) {
                    // Valid - set success icon
                    step.svgIcon = this.successIcon;
                } else if (status === false) {
                    // Invalid - set error icon
                    step.svgIcon = this.errorIcon;
                } else {
                    // Not validated or null - reset to original icon
                    const originalIcon = this.#originalIcons.get(step.label);
                    if (originalIcon) {
                        step.svgIcon = originalIcon;
                    }
                }
            }
        });
    }

    /**
     * Gets the original icon for a step by label
     * @param label The step label
     * @returns The original SVG icon for the step
     */
    getOriginalIcon(label: string): SVGIcon | undefined {
        return this.#originalIcons.get(label);
    }

    /**
     * Resets all step icons to their original state
     */
    resetStepIcons(): void {
        this.steps().forEach(step => {
            const originalIcon = this.#originalIcons.get(step.label);
            if (originalIcon) {
                step.svgIcon = originalIcon;
            }
        });
    }
}
